import { useState } from "react";
import { Link } from "react-router-dom";
import { Lock, Sparkles, ArrowRight, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  computeLockedFeatures,
  PLAN_LABEL,
  PLAN_PRICE,
} from "@/lib/plan-features";

/**
 * Dashboard widget that surfaces features locked on the user's current plan
 * and CTAs to upgrade to the next tier.
 *
 * Renders nothing for:
 *   - users without a plan
 *   - agency users (top tier — nothing to upsell)
 *
 * Behaviour:
 *   - free / starter / pro users see a card showing up to 4 locked features
 *     by default, with a "show all" toggle to expand the full list
 *   - Always includes "Upgrade to {nextPlan}" CTA pointing at /pricing
 */
export default function PlanLockedCard({ user }) {
  const [showAll, setShowAll] = useState(false);
  if (!user || typeof user !== "object") return null;
  const plan = user.plan || "free";

  const { locked, nextPlan } = computeLockedFeatures(plan);
  if (!nextPlan || locked.length === 0) return null;

  const visible = showAll ? locked : locked.slice(0, 4);
  const hiddenCount = locked.length - visible.length;

  return (
    <section
      className="border border-foreground bg-white p-6 md:p-7 mb-10"
      data-testid="plan-locked-card"
    >
      <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-9 h-9 bg-foreground text-background grid place-items-center flex-shrink-0">
            <Lock className="w-4 h-4" strokeWidth={1.75} />
          </div>
          <div className="min-w-0">
            <div className="font-mono-data text-[10px] uppercase tracking-widest text-muted-foreground">
              // your plan · {PLAN_LABEL[plan] || plan}
            </div>
            <h3 className="font-display tracking-tight text-xl mt-0.5">
              {locked.length} {locked.length === 1 ? "feature" : "features"} locked.
            </h3>
            <p className="text-sm text-muted-foreground mt-1 max-w-xl">
              Upgrade to <strong className="text-foreground">{PLAN_LABEL[nextPlan]}</strong> ({PLAN_PRICE[nextPlan]}) to unlock everything below.
            </p>
          </div>
        </div>
        <Link to="/pricing#compare" data-testid="plan-locked-upgrade-cta">
          <Button className="rounded-none bg-foreground hover:bg-foreground/90 whitespace-nowrap">
            <Sparkles className="w-4 h-4 mr-2" />
            Upgrade to {PLAN_LABEL[nextPlan]}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
      </div>

      <ul className="divide-y divide-foreground/10 border-y border-foreground/10" data-testid="plan-locked-list">
        {visible.map((f) => (
          <li
            key={f.label}
            className="py-3 flex items-center justify-between gap-3"
            data-testid={`plan-locked-row-${f.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`}
          >
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{f.label}</div>
              <div className="font-mono-data text-[10px] uppercase tracking-widest text-muted-foreground mt-0.5">
                // {f.group.toLowerCase()}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="font-mono-data text-[10px] uppercase tracking-widest text-muted-foreground line-through">
                {f.currentValue}
              </span>
              <ArrowRight className="w-3 h-3 text-muted-foreground" />
              <span className="font-mono-data text-[10px] uppercase tracking-widest border border-emerald-500 bg-emerald-50 text-emerald-700 px-1.5 py-0.5">
                {f.nextValue || PLAN_LABEL[f.unlockedAt]}
              </span>
            </div>
          </li>
        ))}
      </ul>

      {hiddenCount > 0 && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          className="mt-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
          data-testid="plan-locked-show-all"
        >
          <ChevronDown className="w-3.5 h-3.5" />
          Show {hiddenCount} more
        </button>
      )}
      {showAll && locked.length > 4 && (
        <button
          type="button"
          onClick={() => setShowAll(false)}
          className="mt-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
          data-testid="plan-locked-show-less"
        >
          <ChevronUp className="w-3.5 h-3.5" /> Show less
        </button>
      )}
    </section>
  );
}
